import { formatDate, todayVn } from './dates';
import { storage } from './storage';

/**
 * List filters: date-range presets (computed in Vietnam time) and per-user persistence helpers.
 * The hook lives in `hooks/usePersistentListFilters.ts`; URL/param contract in docs/web.md → "List filters & deep links".
 */

export type DatePreset = 'today' | '7d' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'thisYear';

export const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: 'Hôm nay' },
  { value: '7d', label: '7 ngày qua' },
  { value: 'thisMonth', label: 'Tháng này' },
  { value: 'lastMonth', label: 'Tháng trước' },
  { value: 'thisQuarter', label: 'Quý này' },
  { value: 'thisYear', label: 'Năm nay' },
];

const PRESET_SET = new Set<string>(DATE_PRESETS.map((p) => p.value));
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isDatePreset(v: string | null | undefined): v is DatePreset {
  return !!v && PRESET_SET.has(v);
}

/** Strict yyyy-MM-dd that is a real calendar date. */
export function isDateOnly(v: string | null | undefined): v is string {
  if (!v || !DATE_RE.test(v)) return false;
  const [y, m, d] = v.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function ymd(y: number, m: number, d: number): string {
  // month is 1-based; Date.UTC normalises overflow (e.g. day 0 = last day of previous month)
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

/** Inclusive [from, to] business dates of a preset, relative to "today" in Vietnam. Periods are whole (month/quarter/year). */
export function presetRange(preset: DatePreset, now: Date = new Date()): { from: string; to: string } {
  const today = todayVn(now);
  const [y, m, d] = today.split('-').map(Number);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case '7d':
      return { from: ymd(y, m, d - 6), to: today };
    case 'thisMonth':
      return { from: ymd(y, m, 1), to: ymd(y, m + 1, 0) };
    case 'lastMonth':
      return { from: ymd(y, m - 1, 1), to: ymd(y, m, 0) };
    case 'thisQuarter': {
      const q0 = Math.floor((m - 1) / 3) * 3 + 1;
      return { from: ymd(y, q0, 1), to: ymd(y, q0 + 3, 0) };
    }
    case 'thisYear':
      return { from: ymd(y, 1, 1), to: ymd(y, 12, 31) };
  }
}

export interface DateRangeValue {
  /** Relative preset (stored as is so it rolls forward). Wins over from/to. */
  preset?: string;
  from?: string;
  to?: string;
}

export interface ResolvedRange {
  from?: string;
  to?: string;
  /** Custom range with from > to or malformed dates (not sent to the API). */
  invalid?: boolean;
}

/** Preset → concrete dates; custom → validated from/to. Empty → {} (all time). */
export function resolveDateRange(v: DateRangeValue, now: Date = new Date()): ResolvedRange {
  if (isDatePreset(v.preset)) return presetRange(v.preset, now);
  const from = v.from || undefined;
  const to = v.to || undefined;
  if ((from && !isDateOnly(from)) || (to && !isDateOnly(to))) return { invalid: true };
  if (from && to && from > to) return { from, to, invalid: true };
  return { from, to };
}

export function hasDateRange(v: DateRangeValue): boolean {
  return isDatePreset(v.preset) || !!v.from || !!v.to;
}

/** Trigger label: "Tháng này", "01/11/2026 – 05/11/2026", "Từ 01/11/2026", "Tất cả thời gian". */
export function dateRangeLabel(v: DateRangeValue): string {
  if (isDatePreset(v.preset)) return DATE_PRESETS.find((p) => p.value === v.preset)!.label;
  if (v.from && v.to) return v.from === v.to ? formatDate(v.from) : `${formatDate(v.from)} – ${formatDate(v.to)}`;
  if (v.from) return `Từ ${formatDate(v.from)}`;
  if (v.to) return `Đến ${formatDate(v.to)}`;
  return 'Tất cả thời gian';
}

// ---------------------------------------------------------------------------------------------------------------------
// Persistence: localStorage key `crm.filters.v1.{tenantId}.{userId}.{screenKey}` (lib/storage adds the `crm.` prefix).
// The owner (tenant.user) of the current session is remembered so logout / session expiry / tenant switch can wipe it.

const NS = 'filters.v1.';
const OWNER_KEY = 'filters.owner';

export interface FilterScope {
  tenantId: number | string;
  userId: number | string;
}

export function filterStorageKey(scope: FilterScope, screenKey: string): string {
  return `${NS}${scope.tenantId}.${scope.userId}.${screenKey}`;
}

export function loadListFilters(scope: FilterScope, screenKey: string): Record<string, string> | null {
  const raw = storage.getJson<unknown>(filterStorageKey(scope, screenKey));
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (typeof v === 'string') out[k] = v;
  return out;
}

export function saveListFilters(scope: FilterScope, screenKey: string, values: Record<string, string>): void {
  storage.setJson(filterStorageKey(scope, screenKey), values);
}

/**
 * Remember which user's filters live in this browser. Switching to another user/tenant wipes the previous owner's saved
 * filters (a tenant switch is a new login).
 */
export function setListFilterOwner(scope: FilterScope): void {
  const owner = `${scope.tenantId}.${scope.userId}`;
  const prev = storage.get(OWNER_KEY);
  if (prev && prev !== owner) storage.removeByPrefix(`${NS}${prev}.`);
  storage.set(OWNER_KEY, owner);
}

/** Logout / session expiry: remove every saved list filter of the session's user (all users' if the owner is unknown). */
export function clearListFilters(): void {
  const owner = storage.get(OWNER_KEY);
  storage.removeByPrefix(owner ? `${NS}${owner}.` : NS);
  storage.remove(OWNER_KEY);
}
